/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {Suspense} from 'react';
import {withLocalization} from '@adobe/adobe-intl-react';
import './Shell.scss';

const MEDIUM_MAX_WIDTH = 900;
const SMALL_MAX_WIDTH = 700;

/**
 * Shell Super component for Experience Cloud UI.
 */
var npsContainer = null;

class Shell extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasDeferredComponents: false,
      isNotOutlined: true,
      size: 'L'
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
    this.onResize();
    this.props.deferredComponentsPromise.then(deferredComponents => {
      this.deferredComponents = deferredComponents;
      this.setState({hasDeferredComponents: true});
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  /**
   * Handler for browser resize event.
   * @listens Event window's resize event.
   */
  onResize = () => {
    const width = window.innerWidth;
    const {size} = this.state;
    let newSize;

    if (width > MEDIUM_MAX_WIDTH) {
      newSize = 'L';
    } else if (width <= SMALL_MAX_WIDTH) {
      newSize = 'S';
    } else {
      newSize = 'M';
    }

    if (newSize !== size) {
      this.setState({size: newSize});
    }
  };

  render() {
    const {
      appContainer,
      appId,
      children,
      clientId,
      coachMark,
      currentActiveTenantId,
      customEnvLabel,
      customProfileButtons,
      customSearch,
      darkestTheme,
      defaultComponent,
      enableNewProfilePopover,
      enableThemes,
      enableWorkHub,
      environment,
      featureFlags,
      feedback,
      filteredSandboxes,
      formatMessage,
      getQueryValue,
      globalSearch,
      hasAccountClusterData,
      helpCenterConfig,
      imsOrgs,
      landingpage,
      locale,
      localeData,
      modal,
      noAccess,
      nps,
      onCustomProfileButtonClick,
      onGlobalDialogOpen,
      onImsOrgChange,
      onSetUserTheme,
      onSignOutClick,
      onSolutionClick,
      onUserProfileClick,
      orgToSubOrgsMap,
      profile,
      pulse,
      registerKeyHandler,
      releaseType,
      sandbox,
      selectedImsOrg,
      selectedSideNavItemId,
      selectedSubOrg,
      selectedWorkspaceId,
      serviceEnvironment,
      services,
      settingsService,
      sideNavConfig,
      sideNavEnabled,
      sideNavOpen,
      solutionIcon,
      solutions,
      solutionShortTitle,
      solutionTitle,
      supportsViewportTakeover,
      toggleSideNav,
      translationService,
      userAvatar,
      userToken,
      viewportTakeover,
      workHubResponse,
      workspaces
    } = this.props;
    const {hasDeferredComponents, size} = this.state;
    const ShellHeader = hasDeferredComponents ? this.deferredComponents.ShellHeader : 'header';
    const ShellHeaderProps = hasDeferredComponents ? {
      appContainer,
      appId,
      coachMark,
      currentActiveTenantId,
      customEnvLabel,
      customProfileButtons,
      customSearch,
      darkestTheme,
      enableNewProfilePopover,
      enableThemes,
      enableWorkHub,
      environment,
      feedback,
      formatMessage,
      getQueryValue,
      globalSearch: {
        ...globalSearch,
        SearchWidget: this.deferredComponents.SearchWidget
      },
      hasAccountClusterData,
      helpCenterConfig,
      imsOrgs,
      landingpage,
      locale,
      localeData,
      modal,
      noAccess,
      onCustomProfileButtonClick,
      onGlobalDialogOpen,
      onImsOrgChange,
      onSetUserTheme,
      onSignOutClick,
      onSolutionClick,
      onUserProfileClick,
      orgToSubOrgsMap,
      pulse,
      registerKeyHandler,
      selectedImsOrg,
      selectedSubOrg,
      selectedWorkspaceId,
      services,
      settingsService,
      sideNavEnabled,
      sideNavOpen,
      size,
      solutionIcon,
      solutions,
      solutionShortTitle,
      solutionTitle,
      toggleSideNav,
      translationService,
      userAvatar,
      userEmail: profile.email,
      userFirstName: profile.first_name,
      userLastName: profile.last_name,
      userName: profile.name,
      userTitle: profile.job_function,
      viewportTakeover,
      workHubResponse,
      workspaces
    } : {
      className: `spectrum-Shell ${darkestTheme ? 'spectrum--darkest' : 'spectrum--lightest'}`
    };

    if (hasDeferredComponents && nps && nps.enabled) {
      npsContainer = (
        <React.Suspense fallback={defaultComponent}>
          <this.deferredComponents.NPSWidget
            clientId={clientId}
            darkestTheme={darkestTheme}
            enableThemes={enableThemes}
            force={nps.force}
            locale={locale}
            onSubmit={nps.onSubmit}
            profile={profile}
            sampling={nps.sampling}
            serviceEnvironment={serviceEnvironment}
            settingsService={settingsService}
            showToAdobeUsers={nps.showToAdobeUsers}
            solution={appId}
            solutionTitle={solutionTitle}
            translationService={translationService}
            userToken={userToken}
          />
        </React.Suspense>
      );
    }

    let AlertsContainer = null;
    if (hasDeferredComponents) {
      AlertsContainer = this.deferredComponents.AlertsContainer;
    }

    const sandboxFallback = <div style={{height: '32px'}} />;
    const ShellSideNav = hasDeferredComponents ? this.deferredComponents.ShellSideNav : () => null;
    const ShellSandboxSwitcherBar = hasDeferredComponents ? this.deferredComponents.ShellSandboxSwitcherBar : () => sandboxFallback;

    if (releaseType && (['alpha', 'beta'].indexOf(releaseType) >= 0)) {
      // Capitalize the first letter of alpha or beta for the label
      let releaseName = releaseType.charAt(0).toUpperCase() + releaseType.slice(1);
      ShellHeaderProps.customEnvLabel = formatMessage({
        defaultMessage: releaseName,
        description: 'Custom environment label for alpha or beta release',
        messageId: releaseType});
      ShellHeaderProps.feedback = {
        buttonLabel: formatMessage({
          defaultMessage: releaseName + ' feedback',
          description: 'Label for alpha or beta feedback button',
          messageId: releaseType + 'Feedback'}),
        enabled: true,
        type: 'openFeedback'
      };
    }

    return (
      <div className="spectrum-ShellContainer">
        <ShellHeader {...ShellHeaderProps}>
          {sandbox.enabled && (
            <Suspense fallback={sandboxFallback}>
              {sandbox.sandboxes.length ? (
                <ShellSandboxSwitcherBar
                  filteredSandboxes={filteredSandboxes}
                  onSandboxChange={sandbox.onChange}
                  sandboxes={sandbox.sandboxes}
                  selectedSandbox={sandbox.selected} />
              ) : null}
            </Suspense>
          )}
        </ShellHeader>
        <div className={classNames('spectrum-ShellContainer-content', {
          'can-takeover': supportsViewportTakeover || appContainer,
          'has-appContainer': appContainer,
          'has-sandboxes': sandbox.sandboxes.length > 0,
          'modal-open': modal,
          viewportTakeover
        })}>
          {npsContainer}
          {sideNavEnabled && sideNavOpen && (
            <Suspense fallback={null}>
              <ShellSideNav
                appContainer={appContainer}
                appId={appId}
                env={environment}
                featureFlags={featureFlags}
                formatMessage={formatMessage}
                modal={modal}
                orgId={selectedImsOrg}
                sandboxesPresent={sandbox.enabled && sandbox.sandboxes.length}
                selectedSideNavItemId={selectedSideNavItemId}
                sideNavConfig={sideNavConfig}
                toggleSideNav={toggleSideNav}
                workHubResponse={workHubResponse}
              />
            </Suspense>
          )}
          {children}
          {AlertsContainer &&
            <Suspense fallback={null}>
              <AlertsContainer />
            </Suspense>}
        </div>
      </div>
    );
  }
}

Shell.propTypes = {
  appContainer: PropTypes.string,
  currentActiveTenantId: PropTypes.string,
  customButtons: PropTypes.object,
  customSearch: PropTypes.shape({
    enabled: PropTypes.bool,
    onClick: PropTypes.func,
    open: PropTypes.bool
  }),
  featureFlags: PropTypes.object,
  filteredSandboxes: PropTypes.object,
  hasAccountClusterData: PropTypes.bool,
  helpCenterConfig: PropTypes.shape({
    resources: PropTypes.arrayOf(
      PropTypes.shape({
        href: PropTypes.string,
        icon: PropTypes.string,
        label: PropTypes.string
      })
    )
  }),
  imsOrgs: PropTypes.arrayOf(
    PropTypes.shape({
      imsOrgId: PropTypes.string,
      imsOrgName: PropTypes.string,
      tenantId: PropTypes.string
    })
  ).isRequired,
  landingpage: PropTypes.shape({
    description: PropTypes.string,
    href: PropTypes.string,
    icon: PropTypes.string,
    iconReact: PropTypes.string,
    longname: PropTypes.string,
    name: PropTypes.string,
    visible: PropTypes.bool
  }),
  locale: PropTypes.string,
  noAccess: PropTypes.bool,
  onCustomButtonClick: PropTypes.func,
  onImsOrgChange: PropTypes.func,
  onSignOutClick: PropTypes.func,
  onSolutionClick: PropTypes.func,
  onUserProfileClick: PropTypes.func,
  sandbox: PropTypes.shape({
    enabled: PropTypes.bool,
    onChange: PropTypes.func,
    sandboxes: PropTypes.array,
    selected: PropTypes.object
  }),
  selectedImsOrg: PropTypes.string,
  selectedSubOrg: PropTypes.object,
  selectedWorkspaceId: PropTypes.string,
  services: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      enabled: PropTypes.bool,
      href: PropTypes.string,
      icon: PropTypes.string,
      iconReact: PropTypes.string,
      learnMoreLink: PropTypes.string,
      longname: PropTypes.string,
      name: PropTypes.string,
      visible: PropTypes.bool
    })
  ),
  settingsService: PropTypes.object,
  solutionIcon: PropTypes.string,
  solutions: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      enabled: PropTypes.bool,
      href: PropTypes.string,
      icon: PropTypes.string,
      iconReact: PropTypes.string,
      learnMoreLink: PropTypes.string,
      longname: PropTypes.string,
      name: PropTypes.string,
      visible: PropTypes.bool
    })
  ),
  solutionShortTitle: PropTypes.string,
  solutionTitle: PropTypes.string,
  userAvatar: PropTypes.string,
  userFirstName: PropTypes.string,
  userLastName: PropTypes.string,
  userName: PropTypes.string,
  userTitle: PropTypes.string,
  workspaces: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string
    })
  )
};

const NOOP = () => {};

Shell.defaultProps = {
  appContainer: null,
  currentActiveTenantId: '',
  customButtons: [],
  customSearch: {
    enabled: false,
    onClick: () => {},
    open: false
  },
  hasAccountClusterData: false,
  helpCenterConfig: {},
  landingpage: {},
  locale: 'en-US',
  noAccess: false,
  onCustomButtonClick: NOOP,
  onImsOrgChange: NOOP,
  onSignOutClick: NOOP,
  onSolutionClick: NOOP,
  onUserProfileClick: NOOP,
  sandbox: {
    enabled: false,
    onChange: NOOP,
    sandboxes: [],
    selected: null
  },
  selectedWorkspaceId: '',
  services: [],
  solutions: [],
  solutionShortTitle: 'AEC',
  solutionTitle: 'Adobe Experience Cloud',
  userAvatar: undefined,
  userName: 'Adobe User',
  userTitle: undefined,
  workspaces: []
};

export default withLocalization(Shell, {displayName: 'Shell'});
